import { TrevorComponent } from '../../types';
import React from 'react';
import { DropdownContext } from './DropdownContext';
import { Trigger } from './subcomponents/Trigger';
import { Content } from './subcomponents/Content';
import styled from 'styled-components';

type CompoundComponents = {
  Trigger: typeof Trigger;
  Content: typeof Content;
};

export type TDropdown = TrevorComponent<
  { isInitiallyOpen?: boolean; onClose?: F0 },
  [JSX.Element, JSX.Element]
> &
  CompoundComponents;

export const DropdownRaw: TDropdown = ({
  children,
  className,
  isInitiallyOpen = false,
  onClose,
}) => {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(isInitiallyOpen);
  const toggle = () => setIsOpen((v) => !v);

  const handleOutsideClick = (e: MouseEvent) => {
    if (
      isOpen &&
      !dropdownRef.current?.contains?.(e.target as Node) &&
      !contentRef.current?.contains?.(e.target as Node)
    ) {
      setIsOpen(false);
      onClose?.();
    }
  };

  React.useEffect(() => {
    if (isInitiallyOpen) {
      setIsOpen(true);
    }
  }, [isInitiallyOpen]);

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleOutsideClick);
    } else {
      document.removeEventListener('click', handleOutsideClick);
    }

    return () => document.removeEventListener('click', handleOutsideClick);
  }, [isOpen]);

  const contextValue = React.useMemo(
    () => ({ isOpen, toggle, triggerRef, contentRef }),
    [isOpen],
  );

  return (
    <DropdownContext.Provider value={contextValue}>
      <div ref={dropdownRef} className={className}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
};
DropdownRaw.Trigger = Trigger;
DropdownRaw.Content = Content;

export const Dropdown = styled(DropdownRaw)`
  position: relative;
  width: fit-content;
`;
